"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useUser } from "@clerk/nextjs";
import { MY_TALK_POSTS_HREF, type TalkPost } from "@/lib/community-talk";
import { listMyTalkPosts } from "@/lib/server-actions/community-talk/posts";
import TalkTable from "./TalkTable";

/**
 * "내 글" 보기(JCG-3029). 공개 GET 페이지는 Clerk 컨텍스트가 없을 수 있어 서버가 아니라
 * 클라이언트가 로그인 확인 후 server action(`listMyTalkPosts`)으로 조회한다.
 */
export default function MyTalkPosts() {
  const { isLoaded, isSignedIn } = useUser();
  const [posts, setPosts] = useState<TalkPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let alive = true;
    // 외부 데이터 fetch 구독 패턴(RestorationCommentSection 과 동일).
    (async () => {
      const result = await listMyTalkPosts();
      if (!alive) return;
      if (result.success) setPosts(result.posts);
      else setError(result.error);
    })();
    return () => {
      alive = false;
    };
  }, [isLoaded, isSignedIn]);

  if (!isLoaded) {
    return <p className="py-10 text-center text-sm text-[#706E6E] dark:text-neutral-400">로그인 상태를 확인하는 중…</p>;
  }
  if (!isSignedIn) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50/60 px-4 py-6 text-center dark:border-amber-900/60 dark:bg-amber-950/20">
        <p className="text-sm text-amber-900 dark:text-amber-200">내 글은 로그인 후 볼 수 있어요.</p>
        <Link
          href={`/sign-in?redirect_url=${encodeURIComponent(MY_TALK_POSTS_HREF)}`}
          className="mt-3 inline-flex items-center justify-center rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 dark:bg-amber-400 dark:text-amber-950 dark:hover:bg-amber-300"
        >
          로그인
        </Link>
      </div>
    );
  }
  if (error) return <p role="alert" className="py-10 text-center text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (posts === null) {
    return <p className="py-10 text-center text-sm text-[#706E6E] dark:text-neutral-400">내 글을 불러오는 중…</p>;
  }
  return (
    <>
      <p className="mb-2 text-right text-xs text-[#706E6E] dark:text-neutral-400">내 글 {posts.length}건</p>
      <TalkTable posts={posts} now={new Date()} emptyText="아직 쓴 글이 없어요. 첫 글을 남겨보세요." />
    </>
  );
}
